import {Component, Input, OnInit} from '@angular/core';
import {chartConfig, pieChartConfig} from '../../app.constants';
import {PdfExportService} from '../../services/export-pdf/pdf-export.service';
import {UserService} from '../../services/user.service';
import {TranslateService} from '@ngx-translate/core';
import {PieChartData} from '../../../models/chart';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
  selector: 'achoo-pie-chart',
  templateUrl: './achoo-pie-chart.component.html',
  styleUrls: ['./achoo-pie-chart.component.scss']
})
export class AchooPieChartComponent implements OnInit {
  @Input() chartData: Array<PieChartData>;
  @Input() chartTitle: string | null;
  @Input() width: number;
  @Input() height: number;
  @Input() legend: {
    verticalAlign: string,
    align: string,
    y: number,
    x: number,
    symbolHeight: number,
    symbolWidth: number,
    symbolRadius: number,
    width: number,
    padding: number,
    itemMarginBottom: number,
    useHTML: boolean,
    enabledNavigation: boolean
    navigationActiveColor?: string,
    navigationInactiveColor?: string,
    navigationArrowSize?: number,
    itemHiddenStyle: any,
    floating?: boolean,
  };
  @Input() chartConfig: {
    spacingTop: number,
    spacingLeft: number,
    spacingRight: number,
  };
  @Input() titleStyling: {
    fontSize: number;
  };
  @Input() pieCenter: number[];
  @Input() pieSize: string;
  @Input() pieInnerSize: string;

  @Input() fallbackMsg: string;
  @Input() fallbackTitle: string;
  @Input() listLegendPlaceholder = [];
  @Input() forPayingUser = false;
  @Input() titleTooltip: string;
  @Input() isDemographicPage = false;
  @Input() isUseInInfluencerOverviewReport = false;
  @Input() overridenSeriesConfig: any;
  @Input() overridenPiePlotOptions: any;
  @Input() applyResponsive = true;

  options: object;
  constructor(protected pdfService: PdfExportService,
              protected userService: UserService,
              protected translate: TranslateService,
              protected deviceService: DeviceDetectorService) {
  }

  ngOnInit() {
    if (!this.fallbackMsg) {
      this.fallbackMsg = this.translate.instant('measure.noData');
    }
    const spacingTop = this.chartConfig && this.chartConfig.spacingTop ? this.chartConfig.spacingTop : chartConfig.defaultSpacingTop;
    const spacingLeft = this.chartConfig && this.chartConfig.spacingLeft ? this.chartConfig.spacingLeft : chartConfig.defaultSpacingLeft;
    const enabledNavigation = this.legend.enabledNavigation !== undefined ? this.legend.enabledNavigation : true;
    this.options = {
      chart: {
        type: chartConfig.type.pie,
        width: this.width,
        height: this.height,
        backgroundColor: chartConfig.transparentBackground,
        spacingTop: spacingTop,
        spacingLeft: spacingLeft,
        spacingRight: this.getConfigValue(this.chartConfig && this.chartConfig.spacingRight, chartConfig.defaultSpacingRight),
        style: {
          fontFamily: chartConfig.font.light
        },
      },
      colors: this.userService.chartColors,
      tooltip: {
        enabled: false
      },
      plotOptions: {
        pie: this.getPiePlotOptions(),
        series: {
          animation: !this.pdfService.exportingPdf
        }
      },
      legend: {
        symbolHeight: this.legend.symbolHeight || pieChartConfig.defaultLegendSymbolSize,
        symbolWidth: this.legend.symbolWidth || pieChartConfig.defaultLegendSymbolSize,
        symbolRadius: this.legend.symbolRadius || pieChartConfig.defaultLegendSymbolRadius,
        verticalAlign: this.legend.verticalAlign || 'top',
        y: this.getConfigValue(this.legend.y, this.getLegendY()),
        x: this.legend.x || pieChartConfig.defaultLegendX,
        align: this.legend.align || 'right',
        layout: 'vertical',
        width: this.legend.width || chartConfig.defaultLegendStyle.defaultSize,
        padding: this.legend.padding || pieChartConfig.defaultLegendPadding,
        itemHiddenStyle: this.getConfigValue(this.legend.itemHiddenStyle, { color: '#cccccc' }),
        itemMarginTop: chartConfig.defaultLegendStyle.marginTop,
        maxHeight: this.deviceService.isMobile() ? chartConfig.defaultLegendStyle.mobileSize : chartConfig.defaultLegendStyle.defaultSize,
        navigation: {
          enabled: enabledNavigation,
          activeColor: this.getConfigValue(this.legend.navigationActiveColor, pieChartConfig.defaultNavigationActiveColor),
          inactiveColor: this.getConfigValue(this.legend.navigationInactiveColor, pieChartConfig.defaultNavigationInactiveColor),
          arrowSize: this.getConfigValue(this.legend.navigationArrowSize, pieChartConfig.defaultNavigationArrowSize)
        },
        floating: this.getConfigValue(this.legend.floating, false),
        useHTML: this.legend.useHTML || false,
        labelFormatter: function () {
          // legend text
          let text = this.name;
          const labelMaxCharacterSize = this.labelMaxCharacterSize || chartConfig.legend.labelMaxCharacterSize;
          if (text.length > labelMaxCharacterSize) {
            text = text.slice(0, chartConfig.legend.labelMaxCharacterSize) + '...';
          }
          const labelHtml = '<div class="label-text">' + text + ' ' + '</div>';

          // value text
          let valueDisplay = '';

          if (this.customLegendDisplay) {
            valueDisplay = this.customLegendDisplay;
          } else {
            let percentageDisplay = '-';

            if (!this.isPlaceholder) {
              const yPercentage = this.y * 100;
              const fracDigit = this.fracDigit || 0;
              percentageDisplay = yPercentage.toFixed(fracDigit);
            }
            valueDisplay = `${percentageDisplay}%`;
          }

          const valueHTML = `<div class="percentage">${valueDisplay}</div`;

          return '<div class="legend-label">'
            + labelHtml
            + valueHTML
            + '</div>';
        }
      },
      credits: {
        enabled: false
      },
      title : {
        text : this.chartTitle,
        // REVIEW: Do we need to add this elsewhere?
        style: Object.assign({}, chartConfig.defaultTitleStyling, this.titleStyling),
        align: chartConfig.title.align,
        x: !this.isDemographicPage ? chartConfig.title.padding : chartConfig.title.paddingLeft,
        y: !this.isDemographicPage ? chartConfig.title.padding : chartConfig.title.paddingTop
      },
      series: [this.getSeriesConfig()],
      responsive: this.getResposive()
    };
  }

  get noDataAvailable() {
    if (this.forPayingUser && this.userService.isFreeUser() && !this.userService.isAdmin) {
      return true;
    }
    if (!this.chartData || this.chartData.length === 0) {
      return true;
    }

    return this.chartData.filter(p => p.y > 0).length === 0;
  }

  protected getLegendY() {
    const length = this.chartData ? this.chartData.length : 0;
    return length >= 10 ? 20 : 30;
  }

  private getConfigValue(configValue, defaultValue): any {
    if (configValue === null || configValue === undefined) {
      return defaultValue;
    }

    return configValue;
  }

  private getSeriesConfig() {
    const defaultConfig = {
      data: this.chartData,
      size: this.deviceService.isMobile() ? chartConfig.pieChart.mobileSize : this.pieSize || chartConfig.pieChart.size,
      innerSize: this.pieInnerSize || chartConfig.pieChart.innerSize
    };

    return this.overridenSeriesConfig
      ? { ...defaultConfig, ...this.overridenSeriesConfig }
      : defaultConfig;
  }

  private getPiePlotOptions() {
    const defaultOptions = {
      allowPointSelect: false,
      cursor: 'pointer',
      dataLabels: {
        enabled: false
      },
      showInLegend: true,
      center: this.pieCenter || pieChartConfig.defaultPieCenter
    };

    return this.overridenPiePlotOptions
      ? { ...defaultOptions, ...this.overridenPiePlotOptions }
      : defaultOptions;
  }

  private getResposive() {
    return this.applyResponsive
    ? {
      rules: [{
        condition: {
          maxWidth: pieChartConfig.smallPieSize
        },
        chartOptions: {
          legend: {
            align: 'right',
            verticalAlign: 'top',
            layout: 'vertical',
            x: this.deviceService.isMobile() ? pieChartConfig.mobileLegendX :  pieChartConfig.defaultSmallChartLegendX,
            itemStyle: {
              'fontSize': '11px'
            }
          },
          chart: {
            height: this.height,
            spacingLeft: pieChartConfig.defaultChartSpacingLeft,
            spacingRight: pieChartConfig.defaultChartSpacingRight,
            spacingTop: pieChartConfig.defaultChartSpacingTop,
            marginLeft: this.deviceService.isMobile() ? pieChartConfig.mobileChartMarginLeft : !this.pdfService.exportingPdf && this.isUseInInfluencerOverviewReport ? pieChartConfig.defaultTitleX : pieChartConfig.chartMarginLeft
          },
          title : {
            style: this.deviceService.isMobile() ? chartConfig.mobileTitleStyling : Object.assign({}, chartConfig.defaultTitleStyling, this.titleStyling),
            x: !this.isDemographicPage ? pieChartConfig.defaultTitleX : chartConfig.title.paddingLeft,
            y: pieChartConfig.defaultTitleY
          },
          plotOptions: {
            pie: {
              center: this.deviceService.isMobile() ? pieChartConfig.mobilePieCenter : (this.pieCenter && this.pieCenter.length !== 0) ? this.pieCenter : pieChartConfig.smallPieCenter
            }
          }
        }
      }]
    }
    : {
      rules: []
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/core/achoo-pie-chart/achoo-pie-chart.component.ts